import {
  contarCompras,
  spendEntriesOf,
  totalCash,
  totalCashInflow,
  totalCashOutflow,
  totalInflow,
  totalOutflow,
  totalSpend,
} from '@domain/finance'
import type { Account, Category, CreditCard, Invoice, Transaction } from '@domain/finance'
import { esperaConferencia, precisaDeCategoria } from '@domain/finance'
import { addMonths } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import { absCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { Numero as NumeroDeDisplay } from '@ui/glass/Numero'
import { groupByDay, rotuloDasFuturas, separarFuturos } from './day-groups'
import type { DayGroup } from './day-groups'
import { Dobra } from '@ui/shell/Dobra'
import { GastosPorCategoria } from './GastosPorCategoria'
import { LinhaDeTransacao } from './LinhaDeTransacao'
import { useState } from 'react'
import { FilterBar } from './FilterBar'
import { InvoiceCards } from './InvoiceCards'
import { applyFilters } from './filters'
import { dicaDoAbatimento, vinculosDoMes } from './abatimento/vinculos'
import type { TransactionFilters } from './filters'
import { monthLabel } from './labels'

/**
 * A lista do mês, com os totais no topo.
 *
 * ## O número grande é o GASTO, e isso é decisão 141
 *
 * Até a Fase 3 o topo mostrava um número por visão, e ele era um **líquido** —
 * soma entrada e saída juntas. Com dado real ficou insustentável: em julho a
 * tela dizia **+19.190,80** sob um rótulo que quer dizer "Gastos", enquanto as
 * saídas somavam **−42.241,71**. O número estava certo; o rótulo mentia.
 *
 * Agora cada visão mostra **três**: o gasto grande, e entradas e líquido
 * pequenos ao lado. É o que se pergunta no dia a dia, sem esconder o resto.
 *
 * ## As duas visões continuam lado a lado, e continuam sendo perguntas diferentes
 *
 * - **Competência** — *quanto consumimos neste mês?* `settlement` não está
 *   nessa lista, e a exclusão que de fato trabalha ali é a de `transfer`.
 * - **Caixa** — *quanto saiu da conta neste mês?* **`settlement` entra**, e é
 *   isso que faz o pagamento da fatura contar uma vez, não zero. Trocar as
 *   funções de coluna seria invisível na competência e mudaria o caixa inteiro.
 *
 * ## Uma lista só, agora
 *
 * A versão mínima da Fase 2 mostrava **duas** listas, uma por visão, e isso era
 * de propósito: tornava visível que elas se cruzam sem se somar. Cumprido o
 * papel, `Docs/04-Design/Telas/Transações.md` pede uma só — *"e aí a diferença
 * entre as duas visões volta a viver só nos totais"*. A lista é a de
 * **competência**, que é a visão padrão do produto.
 *
 * ## Filtrar não é recortar
 *
 * Quem recorta o mês são `listByCompetence` e `listByCash`, no adapter, medidas
 * contra `isInCompetence`/`isInCash`. Esta tela **não** refaz o recorte. O que
 * ela filtra é outra coisa — "só não categorizadas" —, e esse filtro entra
 * **antes** dos totais de propósito: a nota diz que *"um total que ignora o
 * filtro visível é uma mentira sutil, e das piores"*.
 */

type Props = {
  month: ReferenceMonth
  competence: Transaction[]
  cash: Transaction[]
  categories: Category[]
  /** As faturas do mês selecionado, e os cartões que lhes dão nome. */
  invoices: Invoice[]
  creditCards: CreditCard[]
  accounts: Account[]
  /** Hoje na casa, para os rótulos "Hoje"/"Ontem". Injetado, nunca lido aqui. */
  today: string
  /** Todos os filtros da tela, lidos da URL pela rota. */
  filters: TransactionFilters
  onFiltersChange: (filters: TransactionFilters) => void
  /** O atalho do estado vazio. Quem navega é a rota; a lista só pede. */
  onSelectMonth: (month: ReferenceMonth) => void
  /**
   * Grava a categoria de uma transação. Resolve quando a escrita terminou;
   * **rejeita** quando falhou, e a lista mostra o erro na própria linha.
   */
  onCategorize: (id: string, categoryId: string | null) => Promise<void>
  /** Uma categoria nasceu do painel de uma linha — o mês inteiro que releia. */
  onCategoriasMudaram: () => void
  /** Confere a categoria que a tabela aplicou, uma ou todas (Fase 10). */
  onConfirmar: (ids: string[]) => Promise<void>
}



// Pela mesma pergunta da fila de Classificar: a entrada ligada segue o gasto,
// e a compra que as entradas zeraram não pede categoria.
const semCategoria = (t: Transaction): boolean => precisaDeCategoria(t)

/**
 * A lista é a **união** das duas visões, sem repetição.
 *
 * Ela não pode ser só a de competência: o pagamento da fatura é `settlement`,
 * não está lá, e sumiria da tela — a transação que mais mexe com o dinheiro do
 * mês seria a única invisível. E não pode ser só a de caixa: a compra no cartão
 * é `deferred`, com `settlementDate` nulo, e também não está.
 *
 * `direct` está nas duas (decisão 96b), então a união precisa de dedupe por
 * `id` — sem ele, todo Pix apareceria duas vezes.
 */
function doMes(competence: Transaction[], cash: Transaction[]): Transaction[] {
  const porId = new Map(competence.map((t) => [t.id, t]))
  for (const t of cash) porId.set(t.id, t)
  return [...porId.values()]
}

/**
 * O bloco de uma visão: o gasto grande, entradas e líquido pequenos.
 *
 * **O gasto é mostrado em módulo.** A soma vem negativa, como as transações que
 * ela soma — o sinal mora no domínio e sai de lá intacto —, e é aqui, na
 * fronteira da tela, que ele vira "R$ 42.241,71" sob o rótulo "Gastos". Fazer o
 * domínio devolver módulo repetiria o erro que a decisão 97(c) pagou.
 */
function TotalsBlock({
  id,
  title,
  outflow,
  inflow,
  net,
}: {
  id: string
  title: string
  outflow: number
  inflow: number
  net: number
}) {
  return (
    <div className="flex-1">
      <h3 id={id} className="text-xs text-[var(--text-muted)]">
        {title}
      </h3>
      {/* Fraunces: é o total do mês, o número que a tela existe para somar.
          `sobreVidro` porque o cartão é `frost`. */}
      <p aria-labelledby={id} className="mt-0.5">
        <NumeroDeDisplay tamanho="medio">
          {formatBRL(absCents(outflow))}
        </NumeroDeDisplay>
      </p>
      <dl className="mt-1 flex gap-3 text-xs text-[var(--text-muted)]">
        <div className="flex gap-1">
          <dt>Entradas</dt>
          <dd className="tabular-nums">{formatBRL(inflow)}</dd>
        </div>
        <div className="flex gap-1">
          <dt>Líquido</dt>
          <dd className="tabular-nums">{formatBRL(net)}</dd>
        </div>
      </dl>
    </div>
  )
}

/**
 * O estado vazio, com o atalho que a nota da tela pede.
 *
 * **Qual atalho depende do porquê da tela estar vazia**, e são dois porquês
 * diferentes: se o filtro está ligado, o mês pode estar cheio e a saída é
 * desligá-lo; se não está, o mês é que está vazio, e a saída é o mês anterior.
 * Oferecer o mês anterior a quem filtrou seria mandar a pessoa procurar num
 * lugar onde ela também não vai achar — o filtro viaja junto na URL.
 *
 * Não existe atalho para o mês seguinte: a tela vazia mais comum é a de um mês
 * que ainda não aconteceu, e o mês anterior é onde o dado está.
 */
function EmptyState({
  month,
  onlyUncategorized,
  onClearFilter,
  onSelectMonth,
}: {
  month: ReferenceMonth
  onlyUncategorized: boolean
  onClearFilter: () => void
  onSelectMonth: (month: ReferenceMonth) => void
}) {
  const anterior = addMonths(month, -1)
  return (
    <p className="text-sm text-[var(--text-muted)]">
      {onlyUncategorized ? (
        <>
          {`Nada sem categoria em ${monthLabel(month)}. `}
          <button type="button" className="alvo justify-start underline" onClick={onClearFilter}>
            Ver todas as transações do mês
          </button>
        </>
      ) : (
        <>
          {`Nenhuma transação em ${monthLabel(month)}. `}
          <button
            type="button"
            className="alvo justify-start underline"
            onClick={() => {
              onSelectMonth(anterior)
            }}
          >
            {`Ver ${monthLabel(anterior)}`}
          </button>
        </>
      )}
    </p>
  )
}

/**
 * A categoria da linha: um rótulo que vira seletor ao ser tocado.
 *
 * **É aqui que as 2.462 sem categoria da base real são resolvidas.** Abrir o
 * detalhe de cada uma seria navegar duas vezes por transação; combinado com o
 * filtro "só não categorizadas", tocar aqui faz a linha **sair da lista** assim
 * que a categoria é escolhida — a tela vira uma fila de trabalho que encurta.
 *
 * O seletor só existe na linha aberta. Noventa e um `<select>` montados de uma
 * vez seriam noventa e um nós a mais no maior mês da base, e a lista é a coisa
 * que precisa rolar bem.
 */
/** As transações fora dos dois totais, para a lista poder marcá-las. */
function transferIds(categories: Category[]): Set<string> {
  return new Set(categories.filter((c) => c.kind === 'transfer').map((c) => c.id))
}

export function TransactionsList({
  month,
  competence,
  cash,
  categories,
  invoices,
  creditCards,
  accounts,
  today,
  filters,
  onFiltersChange,
  onSelectMonth,
  onCategorize,
  onCategoriasMudaram,
  onConfirmar,
}: Props) {
  // **Os filtros entram antes dos totais.** A nota é explícita: *"o total
  // exibido respeita os filtros… um total que ignora o filtro visível é uma
  // mentira sutil, e das piores"*. Vale para os seis, não só para o de
  // categoria — e é por isso que a aplicação é uma chamada só, aqui em cima,
  // em vez de cada bloco filtrar o que lhe convém.
  const onlyUncategorized = filters.onlyUncategorized
  const onlyAuto = filters.onlyAuto
  const competenceView = applyFilters(competence, filters, categories)
  const cashView = applyFilters(cash, filters, categories)

  const comp = spendEntriesOf(competenceView, categories)
  const cx = spendEntriesOf(cashView, categories)

  // A contagem da badge é do mês INTEIRO, não da visão filtrada: ela existe
  // para dizer quanto trabalho falta, e com o filtro ligado ela seria igual ao
  // que já está na tela.
  //
  // **E conta a UNIÃO, não só a competência** — o mesmo universo que a lista
  // desenha. Contar só a competência deixava de fora justamente o pagamento de
  // fatura, que é `settlement`: em agosto a badge dizia "nenhuma pendente" e a
  // lista filtrada mostrava uma linha. Uma contagem que não fecha com a tela
  // logo abaixo dela é pior que contagem nenhuma.
  //
  // **E conta COMPRAS, não linhas.** Uma compra de 12x é uma decisão, não doze:
  // as parcelas dividem categoria (o gatilho `onTransactionWrite` garante), e
  // classificar qualquer uma resolve todas. Medido nesta casa, 462 das linhas
  // sem categoria são 123 compras — dizer o número de linhas transforma um
  // trabalho de minutos num que parece de horas.
  const uncategorized = contarCompras(doMes(competence, cash).filter(semCategoria))

  /**
   * A fila de conferência da Fase 10: o que a tabela de regras categorizou e
   * ninguém olhou ainda.
   *
   * **Do mês inteiro, e não da visão filtrada**, pelo mesmo motivo da badge de
   * não categorizadas: ela existe para dizer quanto trabalho falta, e com o
   * filtro ligado seria igual ao que já está na tela.
   */
  const porConferir = doMes(competence, cash).filter((t) =>
    esperaConferencia(t.enrichment.categorySource),
  )

  // Uma aberta por vez — ver o comentário no `<Row>`.
  const [abertaId, setAbertaId] = useState<string | null>(null)
  const [conferindo, setConferindo] = useState(false)
  const transfers = transferIds(categories)
  // Do mês inteiro, e não da visão filtrada: a dica de um gasto diz quem o
  // abateu mesmo quando o filtro esconde a entrada.
  const vinculos = vinculosDoMes(doMes(competence, cash))
  const groups = groupByDay(doMes(competenceView, cashView), today)
  // O que ainda não aconteceu fica numa dobra FECHADA no topo — ver
  // `separarFuturos`. Os totais não mudam: parcela futura do mês é gasto do
  // mês, e esconder da conta seria outra coisa.
  const { futuros, resto } = separarFuturos(groups, today)

  const diaDaLista = (group: DayGroup) => (
    <div key={group.date} className="mb-3">
      <h3 className="mb-1 text-sm font-semibold tracking-tight">
        {group.relative === null ? group.full : `${group.relative} · ${group.full}`}
      </h3>
      <ul className="text-sm">
        {group.transactions.map((transaction) => (
          <LinhaDeTransacao
            key={transaction.id}
            transaction={transaction}
            transfers={transfers}
            categories={categories}
            creditCards={creditCards}
            accounts={accounts}
            // **Uma aberta por vez.** Duas abertas empurram a lista para
            // baixo e desfazem a densidade que a linha única comprou;
            // e categorizar é uma coisa de cada vez.
            aberta={abertaId === transaction.id}
            onAbrir={() => {
              setAbertaId((atual) => (atual === transaction.id ? null : transaction.id))
            }}
            onCategorize={onCategorize}
            onCategoriasMudaram={onCategoriasMudaram}
            onConfirmar={(id) => onConfirmar([id])}
            dica={dicaDoAbatimento(transaction, vinculos)}
          />
        ))}
      </ul>
    </div>
  )

  return (
    <div>
      {/* Antes dos totais: a fatura é o compromisso que a pessoa quer ver
          primeiro ao abrir o mês, e `Docs/04-Design/Telas/Transações.md` a põe
          acima da lista. */}
      <InvoiceCards invoices={invoices} creditCards={creditCards} />

      <FilterBar
        filters={filters}
        onChange={onFiltersChange}
        categories={categories}
        accounts={accounts}
        creditCards={creditCards}
      />

      {/* O mês uma vez só, e as duas colunas EMPILHAM no estreito.
          Em Fraunces os totais ficaram maiores, e "R$ 62.769,29" ao lado de
          "R$ 51.231,00" estourava a largura em 375px — o segundo saía cortado
          pela borda direita. Visto em produção. */}
      <p className="mb-2 text-xs text-[var(--text-muted)]">{monthLabel(month)}</p>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row">
        {/* Os rótulos "(competência)" e "(caixa)" aparecem SEMPRE, sem exceção
            — Docs/04-Design/Telas/Dashboard.md. São conceitos que se aprende
            uma vez, e o app é quem ensina. */}
        <TotalsBlock
          id="total-competencia"
          title="Gastos (competência)"
          outflow={totalOutflow(comp)}
          inflow={totalInflow(comp)}
          net={totalSpend(comp)}
        />
        <TotalsBlock
          id="total-caixa"
          title="Saídas (caixa)"
          outflow={totalCashOutflow(cx)}
          inflow={totalCashInflow(cx)}
          net={totalCash(cx)}
        />
      </div>

      {/* **Aparece também com o filtro ligado e contagem zero.** Antes a
          condição era só `uncategorized > 0`, e o caso "filtro ligado, nada sem
          categoria no mês" escondia o próprio botão de desligar: a tela dizia
          "Nada sem categoria em Julho" e não havia caminho de volta sem editar
          a URL. Um controle que some justamente quando é a única saída é pior
          que um controle inútil. */}
      {(uncategorized > 0 || onlyUncategorized) && (
        <p className="mb-3 text-sm">
          <button
            type="button"
            aria-pressed={onlyUncategorized}
            className="alvo justify-start underline"
            onClick={() => {
              onFiltersChange({
                ...filters,
                onlyUncategorized: !onlyUncategorized,
                // Os dois juntos dão lista vazia sempre: uma pede categoria
                // nula, o outro pede categoria posta pela tabela.
                onlyAuto: false,
              })
            }}
          >
            {/* O caso de zero com o filtro ligado é o momento em que a fila
                esvazia, e "Mostrando só as 0 não categorizadas" seria uma frase
                ruim justo aí. */}
            {!onlyUncategorized
              ? `${String(uncategorized)} não categorizadas`
              : uncategorized === 0
                ? 'Nada mais sem categoria — ver todas'
                : `Mostrando só as ${String(uncategorized)} não categorizadas — ver todas`}
          </button>
        </p>
      )}

      {/* **Fechada por padrão**, como as futuras: quem abre o mês vem ver a
          lista, e um gráfico de 176px entre os totais e a primeira transação
          empurraria justamente o que a pessoa veio ver. Quem quer o gráfico
          abre uma vez — e o `<details>` lembra enquanto a tela está aberta. */}
      <div className="mb-4">
        <Dobra titulo="Gastos do mês por categoria">
          <GastosPorCategoria transactions={doMes(competence, cash)} categories={categories} />
        </Dobra>
      </div>

      {/* A fila da tabela de regras, ao lado da fila de decidir. As duas são
          contadores-filtro, e a ordem é a do trabalho: primeiro o que ninguém
          decidiu, depois o que o app decidiu e espera um olho. */}
      {(porConferir.length > 0 || onlyAuto) && (
        <p className="mb-3 flex flex-wrap items-center gap-3 text-sm">
          <button
            type="button"
            aria-pressed={onlyAuto}
            className="alvo justify-start underline"
            style={{ color: 'var(--automatico)' }}
            onClick={() => {
              onFiltersChange({ ...filters, onlyAuto: !onlyAuto, onlyUncategorized: false })
            }}
          >
            {onlyAuto
              ? porConferir.length === 0
                ? 'Tudo conferido — ver todas'
                : `Mostrando ${rotuloDaFila(porConferir.length)} — ver todas`
              : rotuloDaFila(porConferir.length)}
          </button>

          {/* Só com o filtro ligado: confirmar quarenta linhas que não estão na
              tela é o tipo de botão que a pessoa aperta sem saber o que aceitou. */}
          {onlyAuto && porConferir.length > 0 && (
            <button
              type="button"
              disabled={conferindo}
              className="alvo justify-start rounded-lg border border-[var(--border)] px-3 text-sm disabled:opacity-45"
              onClick={() => {
                setConferindo(true)
                void onConfirmar(porConferir.map((t) => t.id)).finally(() => {
                  setConferindo(false)
                })
              }}
            >
              {`Confirmar todas (${String(porConferir.length)})`}
            </button>
          )}
        </p>
      )}

      <section aria-label="Transações do mês">
        {groups.length === 0 && (
          <EmptyState
            month={month}
            onlyUncategorized={onlyUncategorized}
            onClearFilter={() => {
              onFiltersChange({ ...filters, onlyUncategorized: false })
            }}
            onSelectMonth={onSelectMonth}
          />
        )}
        {futuros.length > 0 && (
          <div className="mb-3">
            <Dobra titulo={rotuloDasFuturas(futuros)}>{futuros.map(diaDaLista)}</Dobra>
          </div>
        )}
        {resto.map(diaDaLista)}
      </section>
    </div>
  )
}

/** "1 categorizada automaticamente" / "7 categorizadas automaticamente". */
function rotuloDaFila(quantas: number): string {
  return quantas === 1
    ? '1 categorizada automaticamente'
    : `${String(quantas)} categorizadas automaticamente`
}
